/**
 * ECOSMITH Footer Component
 */

class EcoFooter extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  connectedCallback() {
    this.render();
  }

  render() {
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          width: 100%;
          background: #0A0E14;
          border-top: 1px solid rgba(255, 255, 255, 0.05);
        }

        .footer-container {
          width: 100%;
          max-width: 1440px;
          margin: 0 auto;
          padding: 4rem 2rem 2rem;
        }

        .footer-grid {
          display: grid;
          grid-template-columns: 2fr 1fr 1fr 1fr;
          gap: 3rem;
          margin-bottom: 3rem;
        }

        @media (max-width: 968px) {
          .footer-grid {
            grid-template-columns: 1fr 1fr;
            gap: 2rem;
          }
        }

        @media (max-width: 640px) {
          .footer-grid {
            grid-template-columns: 1fr;
          }
        }

        /* Brand Column */
        .footer-brand {
          max-width: 320px;
        }

        .brand-logo {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 1rem;
        }

        .brand-icon {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #2A4B2A 0%, #94B58A 100%);
          border-radius: 10px;
          color: white;
          font-size: 18px;
          font-weight: 800;
        }

        .brand-name {
          font-size: 1.25rem;
          font-weight: 700;
          color: white;
        }

        .brand-tagline {
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.5);
          margin: 0 0 1.5rem 0;
          line-height: 1.6;
        }

        .social-links {
          display: flex;
          gap: 0.75rem;
        }

        .social-link {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 10px;
          color: rgba(255, 255, 255, 0.6);
          transition: all 0.2s ease;
        }

        .social-link:hover {
          background: rgba(74, 222, 128, 0.15);
          border-color: rgba(74, 222, 128, 0.35);
          color: #4ade80;
        }

        .social-link svg {
          width: 20px;
          height: 20px;
        }

        /* Link Columns */
        .footer-column h4 {
          font-size: 0.875rem;
          font-weight: 600;
          color: white;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin: 0 0 1rem 0;
        }

        .footer-links {
          list-style: none;
          margin: 0;
          padding: 0;
        }

        .footer-links li {
          margin-bottom: 0.75rem;
        }

        .footer-links a {
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.5);
          text-decoration: none;
          transition: color 0.2s ease;
        }

        .footer-links a:hover {
          color: #4ade80;
        }

        /* Certifications */
        .certifications {
          display: flex;
          gap: 1rem;
          margin-top: 1.5rem;
        }

        .cert-badge {
          padding: 6px 12px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 6px;
          font-size: 0.75rem;
          color: rgba(255, 255, 255, 0.5);
        }

        /* Bottom Bar */
        .footer-bottom {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding-top: 2rem;
          border-top: 1px solid rgba(255, 255, 255, 0.05);
        }

        @media (max-width: 640px) {
          .footer-bottom {
            flex-direction: column;
            gap: 1rem;
            text-align: center;
          }
        }

        .copyright {
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.4);
        }

        .legal-links {
          display: flex;
          gap: 1.5rem;
        }

        .legal-links a {
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.4);
          text-decoration: none;
          transition: color 0.2s ease;
        }

        .legal-links a:hover {
          color: rgba(255, 255, 255, 0.7);
        }
      </style>

      <footer class="footer-container">
        <div class="footer-grid">
          <div class="footer-brand">
            <div class="brand-logo">
              <img src="/logo.png" alt="Ecosmith Pakistan Logo" style="height: 48px; width: auto;" />
            </div>
            <p class="brand-tagline">
              Precision-engineered hygienic solutions for Pakistan's food, 
              pharmaceutical, and personal care industries.
            </p>
            <div class="social-links">
              <a href="#" class="social-link" aria-label="LinkedIn">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg>
              </a>
              <a href="#" class="social-link" aria-label="Facebook">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
              </a>
              <a href="#" class="social-link" aria-label="YouTube">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z"/><polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"/></svg>
              </a>
            </div>
            <div class="certifications">
              <span class="cert-badge">ISO 9001:2015</span>
              <span class="cert-badge">GMP Compliant</span>
            </div>
          </div>

          <div class="footer-column">
            <h4>Solutions</h4>
            <ul class="footer-links">
              <li><a href="#products">Food Processing</a></li>
              <li><a href="#products">Hygiene Solutions</a></li>
              <li><a href="#products">CIP/SIP Systems</a></li>
              <li><a href="#products">Automation</a></li>
            </ul>
          </div>

          <div class="footer-column">
            <h4>Company</h4>
            <ul class="footer-links">
              <li><a href="#process">Our Process</a></li>
              <li><a href="#jobs">Careers</a></li>
              <li><a href="#resources">Resources</a></li>
              <li><a href="#contact">Contact</a></li>
            </ul>
          </div>

          <div class="footer-column">
            <h4>Support</h4>
            <ul class="footer-links">
              <li><a href="mailto:support@ecosmith.pk">Technical Support</a></li>
              <li><a href="#">Spare Parts</a></li>
              <li><a href="#">Maintenance</a></li>
              <li><a href="#">Training</a></li>
            </ul>
          </div>
        </div>

        <div class="footer-bottom">
          <p class="copyright">© 2026 ECOSMITH Pakistan. All rights reserved.</p>
          <div class="legal-links">
            <a href="#">Privacy Policy</a>
            <a href="#">Terms of Service</a>
          </div>
        </div>
      </footer>
    `;
  }
}

customElements.define('eco-footer', EcoFooter);